/**
 * Pencil shading drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_doodles`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const pencilShadingDrawingsPack: ToilePack = {
    slug: 'pencil-shading-drawings',
    category: 'coloring',
    name: 'Pencil shading drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'spheres', label: 'Shaded shapes', subject: 'a sphere, a cube and a cone on a table, lit from the upper left, each with its highlight, core shadow and cast shadow'},
        {key: 'apple', label: 'Apple', subject: 'an apple with a short stem and one leaf resting on a cloth, its cast shadow soft on the right'},
        {key: 'drapery', label: 'Drapery', subject: 'a cloth draped over a box and falling in deep folds to the table, every fold turning from light to dark'},
    ],
    styles: [
        {key: 'blended', label: 'Smooth blend', style: 'smooth blended graphite with soft gradients and no visible strokes', palette: 'Graphite from white through mid gray to near black.', framing: 'The study centered on the page with white paper around it.'},
        {key: 'hatched', label: 'Hatching', style: 'tone built from hatching and cross-hatching in layers of pencil lines', palette: 'Graphite from white through mid gray to near black.', framing: 'The study centered on the page with white paper around it.'},
        {key: 'contrast', label: 'High contrast', style: 'dramatic high-contrast shading with deep near-black darks and a bright untouched highlight', palette: 'Graphite from white to near black.', framing: 'The study centered on the page with white paper around it.'},
    ],
    directions: ['a pear', 'an egg', 'a glass'],
    directionHint: 'try: a pear',
    seo: {
        title: 'Pencil Shading Drawings: Shapes, Apple, Drapery',
        metaDescription: 'Pencil shading studies for beginners, shaded shapes, an apple and a draped cloth, in smooth blend, hatching and high contrast.',
        h1: 'Pencil shading drawings: shaded shapes, apple, drapery',
        tagline: 'Classic shading subjects, lit from one side, with tones from white to near black.',
        intro: 'Pencil shading drawings are studies made to practice shading, the exercise every drawing class sets: light from one side and a shadow on the table. These nine pages show classic subjects with graphite tones from white to near black: shaded shapes, an apple, and a draped cloth. Each comes as smooth blended graphite, hatching, or high-contrast shading. For another subject, type it in the direction box.',
        useCases: [
            'A shading exercise for a drawing class',
            'A reference for light and shadow',
            'A practice sheet for beginners',
            'A study for a sketchbook',
        ],
        faq: [
            {q: 'Where does the light come from?', a: 'From the upper left in these studies, so each highlight sits on the left and each cast shadow falls to the right.'},
            {q: 'What is the difference between the three styles?', a: 'Smooth blend has no visible strokes, hatching builds tone from layers of lines, and high contrast uses deep darks with a bright untouched highlight.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type it in the direction box, such as a pear, an egg or a glass. A typed subject is a request, so the result may differ from the samples.'},
        ],
    },
}
